import { useState } from 'react';
import { Calendar, ChevronDown, ExternalLink, Info } from 'lucide-react';
import { Button, ScrollArea, Separator, Tabs, TabsContent, TabsList, TabsTrigger, cn } from 'shared-ui';

import { FindAndFixWordmark } from '@/components/shared/FindAndFixWordmark';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { StatusPill } from '@/components/shared/StatusPill';
import { getLeakStatusPillPresentation } from '@/components/shared/statusPillPresentation';
import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import { PoiHistoryTab } from './poi-history/PoiHistoryTab';
import { PoiHistoryEventDetail } from './poi-history/PoiHistoryEventDetail';
import {
    formatPipeType,
    formatRepaired,
    formatSearchDate,
    getLeakageRateDisplay,
    formatLeakageRateMlD,
} from '../utils/poiFeatureProperties';
import type { PoiFeatureProperties } from '../utils/poiFeatureProperties';
import { PoiPanelField, PoiWhat3WordsField } from './PoiPanelFields';

// The Find & Fix leak details panel. Renders entirely from the selected leak feature's
// properties — the server resolves the backing record (a root's derived first leak or a
// child row, and later a re-sourced record) at projection time, so this panel never
// interprets the persistence shape. `source_poi_id` is used for exactly one thing: the
// family history fetch, whose endpoint accepts any family member's row id.
//
// Interim layout pending the designed treatment: mirrors the POI panel's field styling so
// the two read as siblings, without the POI-only affordances (worklist Previous/Next
// navigation stays a POI concept; clipboard copy follows with the design).

interface LeakDetailsPanelProps {
    leak: PoiFeatureProperties | null;
    /**
     * The client the leak belongs to, for the per-record reads (history). The selected client
     * in Find & Fix; in the Network Explorer the record's own client, since records of several
     * clients share one map.
     */
    clientId?: string | null;
    isOpen: boolean;
    onClose: () => void;
    /** When present, the footer offers to open this record in its client's Find & Fix. */
    onOpenInFindAndFix?: () => void;
}

function PanelContent({
    leak,
    clientId,
    onClose,
    onOpenInFindAndFix,
}: {
    leak: PoiFeatureProperties;
    clientId: string | null;
    onClose: () => void;
    onOpenInFindAndFix?: () => void;
}) {
    const leakage = getLeakageRateDisplay(leak);
    const pipeType = formatPipeType(leak.pipe_type);
    const formattedSurveyedDate = formatSearchDate(leak.surveyed_date);
    const formattedSearchDate = formatSearchDate(leak.search_date);
    const formattedRepaired = formatRepaired(leak.repaired);
    const stopLabel = leak.suffix_label && leak.suffix_label.length > 0 ? leak.suffix_label : String(leak.stop);
    const historyPoiId = leak.source_poi_id ?? leak.family_poi_id ?? leak.id;

    const [activeTab, setActiveTab] = useState('details');
    const [hasActivatedHistory, setHasActivatedHistory] = useState(false);
    const [detailEvent, setDetailEvent] = useState<PoiHistoryEvent | null>(null);

    // Reset the lazy history fetch and any open event detail when the selected leak
    // changes (render-time derived state, matching the POI panel's idiom).
    const [prevLeakId, setPrevLeakId] = useState(leak.id);
    if (leak.id !== prevLeakId) {
        setPrevLeakId(leak.id);
        setHasActivatedHistory(activeTab === 'history');
        setDetailEvent(null);
    }

    return (
        <>
            <div className="flex items-start justify-between px-6 pt-6 pb-3">
                <div className="space-y-2">
                    <h2 className="text-xl font-black uppercase">Find &amp; Fix Leak</h2>
                    {/* Nothing in the explorer names a record's owning client: not this header
                        and not the hover card (design decision). */}
                    <StatusPill presentation={getLeakStatusPillPresentation(leak.status)} />
                </div>
                <PanelCloseButton onClick={onClose} aria-label="Close leak details" />
            </div>
            <Separator />
            <Tabs
                value={activeTab}
                onValueChange={(value) => {
                    setActiveTab(String(value));
                    if (value === 'history') setHasActivatedHistory(true);
                }}
                className={cn('min-h-0 flex-1 flex-col', detailEvent === null ? 'flex' : 'hidden')}
            >
                <TabsList variant="line" className="poi-panel-tabs-list w-full rounded-none border-b p-0">
                    <TabsTrigger value="details" className="text-primary data-active:text-primary flex-1">
                        Details
                    </TabsTrigger>
                    <TabsTrigger value="history" className="text-primary data-active:text-primary flex-1">
                        History
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <ScrollArea className="min-h-0 flex-1">
                        <div className="space-y-4 px-6 py-4">
                            <PoiPanelField label="Worklist">
                                <span className="flex-1 text-sm">{leak.name}</span>
                            </PoiPanelField>
                            <PoiPanelField label="Stop">
                                <span className="flex-1 text-sm">{stopLabel}</span>
                            </PoiPanelField>
                            <PoiWhat3WordsField label="Leak What3words" meta="(Actual)" value={leak.leak_what3words} />
                            <PoiWhat3WordsField
                                label="Point of Interest What3words"
                                meta="(Original)"
                                value={leak.what3words}
                            />
                            <PoiPanelField label="Pipe Type">
                                <Info className="text-primary size-4 shrink-0 stroke-[1.5]" aria-hidden="true" />
                                <span className="flex-1 text-sm">{pipeType ?? '—'}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Leakage Rate" meta={`(${leakage.provenance})`}>
                                <span className="poi-panel-control__unit">Ml/D</span>
                                <Separator orientation="vertical" aria-hidden="true" />
                                <span className="flex-1 text-sm">{formatLeakageRateMlD(leakage.value)}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Surveyed Date">
                                <span
                                    className={cn('flex-1 text-sm', !formattedSurveyedDate && 'text-muted-foreground')}
                                >
                                    {formattedSurveyedDate ?? 'DD/MM/YYYY'}
                                </span>
                                <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Date of Search">
                                <span className={cn('flex-1 text-sm', !formattedSearchDate && 'text-muted-foreground')}>
                                    {formattedSearchDate ?? 'DD/MM/YYYY'}
                                </span>
                                <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                            <PoiPanelField label="Repaired?">
                                <span className="flex-1 text-sm">{formattedRepaired ?? '—'}</span>
                                <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />
                            </PoiPanelField>
                        </div>
                    </ScrollArea>
                    {onOpenInFindAndFix && (
                        <div className="shrink-0 border-t px-6 py-4">
                            <Button
                                variant="outline"
                                type="button"
                                className="h-10 w-full cursor-pointer"
                                onClick={onOpenInFindAndFix}
                            >
                                <ExternalLink className="size-4" aria-hidden="true" />
                                {/* One inline run, so "Open in" and the wordmark share a baseline. */}
                                <span>
                                    Open in <FindAndFixWordmark />
                                </span>
                            </Button>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="history" className="mt-0 flex min-h-0 flex-1 flex-col">
                    <PoiHistoryTab
                        poiId={historyPoiId}
                        clientId={clientId}
                        enabled={hasActivatedHistory}
                        onViewDetails={setDetailEvent}
                    />
                </TabsContent>
            </Tabs>

            {detailEvent !== null && (
                <PoiHistoryEventDetail
                    event={detailEvent}
                    onBack={() => {
                        setDetailEvent(null);
                    }}
                />
            )}
        </>
    );
}

export function LeakDetailsPanel({
    leak,
    clientId = null,
    isOpen,
    onClose,
    onOpenInFindAndFix,
}: LeakDetailsPanelProps) {
    const [lastLeak, setLastLeak] = useState<PoiFeatureProperties | null>(leak ?? null);
    const [prevLeak, setPrevLeak] = useState(leak);
    if (leak !== prevLeak) {
        setPrevLeak(leak);
        if (leak !== null) setLastLeak(leak);
    }

    return (
        <SlidingPanel isOpen={isOpen} onClose={onClose} ariaLabel="Leak details" title="Find & Fix Leak Details">
            {lastLeak && (
                <PanelContent
                    leak={lastLeak}
                    clientId={clientId}
                    onClose={onClose}
                    onOpenInFindAndFix={onOpenInFindAndFix}
                />
            )}
        </SlidingPanel>
    );
}
